import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'es' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Traducciones estáticas (se pueden mover a archivos JSON separados)
export const translations = {
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.about': 'Nosotros',
    'nav.services': 'Servicios',
    'nav.products': 'Productos',
    'nav.distributors': 'Distribuidores',
    'nav.blog': 'Blog',
    'nav.contact': 'Contacto',
    'nav.careers': 'Carreras',
    
    // Header Actions
    'header.newProject': 'Nuevo Proyecto',
    'header.enterpriseSolutions': 'Soluciones Empresariales',
    'header.contact': 'Contacto',
    
    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.close': 'Cerrar',
    'common.continue': 'Continuar',
    'common.back': 'Atrás',
    'common.next': 'Siguiente',
    'common.previous': 'Anterior',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.clear': 'Limpiar',
    'common.reset': 'Restablecer',
    'common.submit': 'Enviar',
    'common.download': 'Descargar',
    'common.upload': 'Subir',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.view': 'Ver',
    'common.share': 'Compartir',

    'common.all_in': 'Todo en',
    'common.more': 'más',
    'common.read_more': 'Ver menos características',
    'common.read_less': 'Ver más características',
    
    // Product Catalog
    'catalog.title': 'Catálogo de Productos',
    'catalog.subtitle': 'Descubre nuestra selección de productos tecnológicos',
    'catalog.allCategories': 'Todas las categorías',
    'catalog.filters.application': 'Aplicación/Industria',
    'catalog.filters.processor': 'Procesador',
    'catalog.filters.connectivity': 'Conectividad',
    'catalog.filters.temperature': 'Rango de temperatura',
    'catalog.filters.clear': 'Limpiar filtros',
    'catalog.filters.clearAdvanced': 'Limpiar filtros avanzados',
    'catalog.results': 'Mostrando {count} de {total} productos',
    'catalog.noResults': 'No se encontraron productos en esta categoría',
    'catalog.viewSpecs': 'Ver especificaciones',
    'catalog.download': 'Descargar Datasheet',
    'catalog.inStock': 'En Stock',
    'catalog.outOfStock': 'Agotado',
    'catalog.comingSoon': 'Próximamente',
    'catalog.specifications': 'Especificaciones Técnicas',
    'catalog.features': 'Características Principales',
    'catalog.applications': 'Aplicaciones',
    
    // Footer
    'footer.company': 'Empresa',
    'footer.services': 'Servicios',
    'footer.support': 'Soporte',
    'footer.legal': 'Legal',
    'footer.followUs': 'Síguenos',
    'footer.newsletter': 'Boletín',
    'footer.newsletterText': 'Suscríbete para recibir las últimas noticias',
    'footer.privacyPolicy': 'Política de Privacidad',
    'footer.termsOfService': 'Términos de Servicio',
    'footer.cookiePolicy': 'Política de Cookies',
    
    // Cookie Consent
        // Cookie Consent
    'cookies.title': 'Uso de Cookies',
    'cookies.message': 'Utilizamos cookies para mejorar su experiencia de navegación.',
    'cookies.accept': 'Aceptar',
    'cookies.decline': 'Rechazar',
    
    // Header buttons
    'buttons.new_project': 'Nuevo Proyecto',
    'buttons.enterprise_solutions': 'Soluciones Empresariales',
    'buttons.contact': 'Habla con un comercial',
    'buttons.cookies': 'Cookies',
    
    // Products
    'products.vending_machine': 'Vending Machine',
    'products.iot_meter': 'IoT Meter',
    'products.plannycs': 'Plannycs',
    'products.edge_platform': 'Edge Platform',
    'products.edge_platform_description': 'Plataforma propietaria de gestión y monitoreo de dispositivos edge',
    'products.integrated_solutions': 'Soluciones y Experiencias Integradas',
    'products.specialized_components': 'Componentes y Accesorios Especializados',
    'products.after_sales_service': 'Postventa y Servicio Técnico Local',
    'products.system_integration': 'System Integration & Servicios de Ingeniería',
    'products.cybersecurity': 'Cybersecurity y Seguridad Industrial',
    'products.engineering_solutions': 'Engineering Solutions & Servicios Think Embedded',
    'products.ai_machine_learning': 'Plataformas de AI y Machine Learning',
    'products.cloud_edge_computing': 'Servicios Cloud y Edge Computing',
    'products.training_development': 'Capacitación y Desarrollo de Competencias',
    'products.regulatory_compliance': 'Regulatory Compliance y Certificación de Estándares',
    
    // Services
    'services.tech_stack': 'Stack Tecnológico',
    'services.edge_management_platform': 'Edge Management Platform',
    'services.technical_consulting': 'Consultoría Técnica',
    'services.technical_service': 'Servicio Técnico',
    'services.custom_solutions': 'Soluciones Personalizadas',
    'services.technical_quote': 'Cotización Técnica',
    
    // Company
    'company.compliance': 'Compliance',
    'company.supplier_portal': 'Portal Proveedores',
    'company.supplier_registration': 'Registro Proveedores',
    'company.client_onboarding': 'Onboarding Clientes',
    'company.knowledge_center': 'Centro de Conocimiento',
    'company.vertical_solutions': 'Soluciones por Vertical',
    'company.innovation_lab': 'Innovation Lab',
    'company.quality_certifications': 'Certificaciones y Calidad',
    'company.white_papers': 'White Papers',
    'company.distributors': 'Distribuidores',
    'company.seco_distributors': 'SECO Distributors',
    'company.fannal_distributors': 'FANNAL Distributors',
    'company.udoo_distributors': 'UDOO Distributors',
    'company.partners': 'Partners',
    'company.customers': 'Clientes',
    'company.faq' : 'Preguntas Frecuentes',
    
    // Industries
    'industries.industry_40': 'Industry 4.0',
    'industries.healthcare': 'Healthcare',
    'industries.smart_infrastructure': 'Smart Infrastructure',
    'industries.transportation': 'Transportation',
    'industries.retail_hospitality': 'Retail & Hospitality',
    
    // Blog
    'blog.edge_ai_manufacturing': 'Edge AI en Manufactura',
    'blog.iot_industrial_case_study': 'Case Study IoT Industrial',
    'blog.critical_firmware_development': 'Desarrollo de Firmware Crítico',
    'blog.mining_solutions': 'Soluciones para Minería',
    
    // Social Media
    'social.twitter': 'Twitter',
    'social.facebook': 'Facebook',
    'social.instagram': 'Instagram',
    'social.linkedin': 'LinkedIn',
    
    // Common
    'common.technical_glossary': 'Glosario Técnico',
    'common.glossary': 'Glosario',
    'common.pricing': 'Precios',
    'common.termsPrivacyForm' : 'Para ver como manejamos la información enviada, refírase a nuestra',
    'common.addressCommercial': 'Dirección Comercial',
    'common.emails': 'Correos Electrónicos',
    'common.phones': 'Teléfonos y Mesa de Soporte',
    'common.quotesPage': 'Cotizaciones de Productos',

    // Language Selector
    'language.current': 'Idioma actual',
    'language.changeTo': 'Cambiar a',
    'language.spanish': 'Español',
    'language.english': 'English',
    
    // Navigation Sections
    'navigation.home': 'Inicio',
    'navigation.about': 'Sobre Nosotros',
    'navigation.services': 'Servicios',
    'navigation.products': 'Productos y Servicios',
    'navigation.company': 'Empresa',
    'navigation.resources': 'Recursos',
    'navigation.industry': 'Industrias',
    'navigation.blog': 'Blog Técnico',
    'navigation.careers': 'Carreras',
    'navigation.more': '...',
    
    // Navigation Labels & Descriptions
    'navigation.company.about.label': 'Sobre Nosotros',
    'navigation.company.about.description': 'Conoce más de nuestra empresa',
    'navigation.company.compliance.label': 'Compliance',
    'navigation.company.compliance.description': 'Cumplimiento y normativas empresariales',
    'navigation.company.careers.label': 'Carreras',
    'navigation.company.careers.description': 'Oportunidades de carrera en Polarycs',
    'navigation.products.view_all.title': 'Ver todos los productos',
    'navigation.products.view_all.subtitle': 'Descubre nuestra gama de productos y servicios especializados',
    'navigation.products.edge_platform.title': 'Plataforma Edge',
    'navigation.products.edge_platform.description': 'Plataforma propietaria de gestión y monitoreo de dispositivos edge',
    'navigation.products.integrated_solutions.label': 'Soluciones y Experiencias Integradas',
    'navigation.products.integrated_solutions.description': 'Soluciones integrales personalizadas para tu negocio',
    'navigation.products.specialized_components.label': 'Componentes y Accesorios Especializados',
    'navigation.products.specialized_components.description': 'Hardware y componentes de alta calidad para IoT',
    'navigation.products.after_sales.label': 'Postventa y Servicio Técnico Local',
    'navigation.products.after_sales.description': 'Soporte técnico especializado y mantenimiento',
    'navigation.products.system_integration.label': 'System Integration & Servicios de Ingeniería',
    'navigation.products.system_integration.description': 'Integración de sistemas y consultoría especializada',
    'navigation.products.cybersecurity.label': 'Cybersecurity y Seguridad Industrial',
    'navigation.products.cybersecurity.description': 'Protección avanzada para sistemas industriales',
    'navigation.products.engineering_solutions.label': 'Soluciones y servicios Think Embedded',
    'navigation.products.engineering_solutions.description': 'Soluciones de ingeniería y desarrollo embebido avanzado',
    'navigation.products.ai_machine_learning.label': 'Plataformas de AI y Machine Learning',
    'navigation.products.ai_machine_learning.description': 'Inteligencia artificial y aprendizaje automático aplicado',
    'navigation.products.cloud_edge.label': 'Servicios Cloud y Edge Computing',
    'navigation.products.cloud_edge.description': 'Computación en la nube y procesamiento en el borde',
    'navigation.products.training.label': 'Capacitación y Desarrollo de Competencias',
    'navigation.products.training.description': 'Formación especializada en tecnologías IoT e Industria 4.0',
    'navigation.products.regulatory.label': 'Soporte regulatorio y cumplimiento de estándares',
    'navigation.products.regulatory.description': 'Cumplimiento normativo y certificación de estándares.',
    'navigation.products.project_consulting.label': 'Consultoría y acompañamiento de proyectos',
    'navigation.products.project_consulting.description': 'Asesoría experta para el éxito de tus proyectos tecnológicos',
    'navigation.resources.glossary.label': 'Glosario',
    'navigation.resources.glossary.description': 'Términos técnicos y definiciones especializadas',
    'navigation.resources.faq.label': 'Preguntas Frecuentes',
    'navigation.resources.faq.description': 'Respuestas a preguntas comunes sobre nuestros servicios',
    'navigation.resources.supplier_registration.label': 'Registro de Proveedores',
    'navigation.resources.tools.label': 'Herramientas',
    'navigation.resources.tools.description': 'Varias herramientas y utilidades para nuestros servicios',
    'navigation.resources.supplier_registration.description': 'Portal para proveedores y socios comerciales',
    'navigation.resources.client_onboarding.label': 'Onboarding Clientes',
    'navigation.resources.client_onboarding.description': 'Proceso de incorporación de nuevos clientes',
    'navigation.industries.industry40.label': 'Industry 4.0',
    'navigation.industries.industry40.description': 'Automatización, mantenimiento predictivo, control de calidad con IA',
    'navigation.industries.healthcare.label': 'Healthcare',
    'navigation.industries.healthcare.description': 'Dispositivos médicos, monitoreo de pacientes, diagnóstico por imágenes',
    'navigation.industries.smart_infrastructure.label': 'Smart Infrastructure',
    'navigation.industries.smart_infrastructure.description': 'Automatización de edificios, gestión energética, monitoreo ambiental',
    'navigation.industries.transportation.label': 'Transportation',
    'navigation.industries.transportation.description': 'Sistemas autónomos, gestión de flotas, telemática avanzada',
    'navigation.industries.retail.label': 'Retail & Hospitality',
    'navigation.industries.retail.description': 'Cartelería digital, analítica de clientes, inventario automatizado',
    'navigation.whitepaper.download': 'Descárgalo ahora',
    'navigation.whitepaper.title': 'Polarycs White Paper IoT',
    
    // Footer
    'footer.heading.title': 'Conoce como modernizar tu empresa',
    'footer.heading.cta': 'Inicia tu proyecto hoy mismo',
    'footer.card1.title': '¿Tienes un proyecto industrial desafiante? Obtén una cotización técnica gratuita en 24 horas',
    'footer.card1.link': 'Cotizar mi proyecto ahora',
    'footer.card2.title': 'Descubre cómo transformamos empresas con tecnología de vanguardia - Cases studies reales',
    'footer.card2.link': 'Explorar casos de éxito',
    'footer.statement': 'Transformamos la industria y el retail con soluciones embebidas, edge computing e integración tecnológica, combinando innovación y soporte especializado para maximizar el valor de tu empresa.',
    'footer.address_cl': 'Av. Concon Reñaca 4000, Concón, Chile',
    'footer.address_au': '223 Liverpool Street, Darlinghurst NSW 2010, Australia',
    'footer.phone': '+123 456 7890',
    'footer.email': 'contacto@polarycs.com',
    'footer.section.company': 'Empresa',
    'footer.section.industries': 'Industrias',
    'footer.section.resources': 'Recursos',
    'footer.tracking_dhl' : 'Rastreo DHL',
    'footer.section.legal': 'Legal',
    'footer.products_services': 'Productos y servicios',
    'footer.disclaimer': 'Polarycs S.p.A. - Todos los derechos reservados © {currentYear} - La información contenida en este sitio web es de carácter informativo y puede estar sujeta a cambios sin previo aviso. Todas las marcas comerciales, nombres de productos, logos y marcas registradas mencionadas en este sitio web son propiedad de sus respectivos propietarios y se utilizan únicamente con fines informativos e identificativos. SECO®, FANNAL®, UDOO®, Intel®, ARM®, Qualcomm®, NVIDIA®, Microsoft®, Amazon Web Services®, Google Cloud®, y otras marcas son propiedad de sus respectivas empresas. Polarycs S.p.A. actúa como distribuidor autorizado y proveedor de soluciones tecnológicas, manteniendo acuerdos comerciales con diversos fabricantes y proveedores de tecnología a nivel mundial. Los precios, disponibilidad de productos y especificaciones técnicas están sujetos a cambios sin previo aviso. Las imágenes de productos mostradas son referenciales y pueden diferir del producto final. Este sitio web y todo su contenido están protegidos por las leyes de propiedad intelectual chilenas e internacionales. Queda prohibida la reproducción total o parcial de cualquier material sin autorización expresa por escrito de Polarycs S.p.A. Para consultas sobre licencias, permisos o uso de contenido, contactar a legal@polarycs.com.',

    // Products
    'products.cart': 'Carro de Cotización',
    'products.plural': 'productos',
    'products.singular': 'producto',
    'products.remove': 'Eliminar',
    'products.add_to_cart': 'Agregar al Carrito',
    'products.remove_from_cart': 'Eliminar del Carrito',
    'products.quantity': 'Cantidad',
    'products.total': 'Total',
    'products.price': 'Precio',
    'products.empty_cart': 'Tu carrito está vacío',
    'products.proceed_to_checkout': 'Proceder a Cotización',
    'products.send_quote': 'Enviar Solicitud de Cotización',
    'products.quote_request': 'Solicitud de Cotización',
    'products.sending': 'Enviando...',
    'products.clean_cart': 'Limpiar Carrito',
    'products.cancel': 'Cancelar',
    'products.resume': 'Resumen del pedido',
    'products.continue_shopping': 'Añade productos para solicitar una cotización',
    'products.form_contact': 'Información de Contacto',
    'products.form_name': 'Nombre',
    'products.form_lastname': 'Apellido',
    'products.form_email': 'Correo Electrónico',
    'products.form_phone': 'Teléfono',
    'products.form_country': 'País',
    'products.form_company': 'Empresa',
    'products.form_company_placeholder': 'Nombre de tu empresa',
    'products.form_message': 'Mensaje Adicional',
    'products.form_message_placeholder': 'Comentarios adicionales o especificaciones especiales...',
    'products.form_quantity': 'Cantidad',
    'products.form_required': 'Este campo es obligatorio',
    'products.form_invalid_email': 'Correo electrónico inválido',
        'products.thank_you': '¡Gracias por tu solicitud!',
    'products.confirmation_message': 'Hemos recibido tu solicitud de cotización. Nos pondremos en contacto contigo pronto.',
    
    // Demo Modal
    'demo.modal.title': 'Solicitar Demo de Edge Platform',
    'demo.modal.subtitle': 'Agende una demostración personalizada con nuestro equipo',
    'demo.personal.info': 'Información Personal',
    'demo.company.info': 'Información de la Empresa',
    'demo.plan.interest': 'Plan de Interés',
    'demo.plan.subtitle': 'Selecciona el plan que mejor se adapte a tus necesidades',
    'demo.form.name': 'Nombre Completo',
    'demo.form.name.placeholder': 'Ej: Juan Pérez',
    'demo.form.email': 'Email Corporativo',
    'demo.form.email.placeholder': 'juan@empresa.com',
    'demo.form.phone': 'Teléfono',
    'demo.form.phone.placeholder': '+56 9 1234 5678',
    'demo.form.country': 'País',
    'demo.form.country.placeholder': 'Seleccionar país',
    'demo.form.role': 'Cargo/Posición',
    'demo.form.role.placeholder': 'Seleccionar cargo',
    'demo.form.company': 'Nombre de la Empresa',
    'demo.form.company.placeholder': 'Ej: TechCorp S.A.',
    'demo.form.team.size': 'Tamaño del Equipo',
    'demo.form.team.size.placeholder': 'Seleccionar',
    'demo.form.industry': 'Industria',
    'demo.form.industry.placeholder': 'Seleccionar industria',
    'demo.form.message': 'Mensaje Adicional',
    'demo.form.message.placeholder': 'Cuéntanos sobre tu proyecto específico, necesidades o preguntas que tienes sobre Edge Platform...',
    'demo.form.required': 'es requerido',
    'demo.form.invalid.email': 'Email inválido',
    'demo.form.select.plan': 'Selecciona un plan de interés',
    'demo.countries.us': 'Estados Unidos',
    'demo.countries.cl': 'Chile',
    'demo.countries.ar': 'Argentina',
    'demo.countries.co': 'Colombia',
    'demo.countries.pe': 'Perú',
    'demo.countries.mx': 'México',
    'demo.countries.ca': 'Canadá',
    'demo.countries.au': 'Australia',
    'demo.countries.other': 'Otro',
    'demo.roles.ceo': 'CEO/Founder',
    'demo.roles.cto': 'CTO',
    'demo.roles.it.director': 'Director de IT',
    'demo.roles.product.manager': 'Product Manager',
    'demo.roles.engineer': 'Ingeniero',
    'demo.roles.operations': 'Gerente de Operaciones',
    'demo.roles.other': 'Otro',
    'demo.team.1-10': '1-10 empleados',
    'demo.team.11-50': '11-50 empleados',
    'demo.team.51-200': '51-200 empleados',
    'demo.team.201-500': '201-500 empleados',
    'demo.team.500+': '500+ empleados',
    'demo.industries.retail': 'Retail',
    'demo.industries.manufacturing': 'Manufactura',
    'demo.industries.agriculture': 'Agricultura',
    'demo.industries.logistics': 'Logística',
    'demo.industries.healthcare': 'Healthcare',
    'demo.industries.fintech': 'FinTech',
    'demo.industries.energy': 'Energía',
    'demo.industries.other': 'Otra',
    'demo.success.title': '¡Solicitud Enviada!',
    'demo.success.message': 'Hemos recibido tu solicitud de demo. Un ejecutivo de Polarycs se pondrá en contacto contigo dentro de las próximas 24 horas para coordinar la demostración personalizada.',
    'demo.success.email': 'Revisa tu email para más detalles sobre el siguiente paso.',
    'demo.button.request': 'Solicitar Demo',
    'demo.button.sending': 'Enviando...',
    
    // Distributors
    'distributors.selectApplication': 'Selecciona una aplicación/industria',
    'distributors.selectProcessor': 'Selecciona un procesador',
    'distributors.selectConnectivity': 'Selecciona una conectividad',
    'distributors.selectTemperature': 'Selecciona un rango de temperatura',
    'distributors.application': 'Aplicación/Industria',
    'distributors.processor': 'Procesador',
    'distributors.connectivity': 'Conectividad',
    'distributors.temperature': 'Rango de temperatura',
    'distributors.clearFilters': 'Limpiar filtros',
    'distributors.clearAdvancedFilters': 'Limpiar filtros avanzados',
    'distributors.viewSpecs': 'Ver especificaciones',
    'distributors.download': 'Descargar Datasheet',
    'distributors.inStock': 'En Stock',
    'distributors.outOfStock': 'Agotado',
    'distributors.comingSoon': 'Próximamente',
    'distributors.showProductsCount': 'Mostrando {count} de {total} productos',
    'distributors.noProductsFound': 'No hay productos disponibles en esta categoría',
    'distributors.principalFeatures': 'Características Principales',
    'distributors.specifications': 'Especificaciones Técnicas',
    'distributors.details' : 'Detalles',
    'distributors.add': 'Agregar al Carrito',
    'distributors.partNumbers': 'Números de Parte (SN\'s)',
    'distributors.partNumberHeader': 'Número de Parte',
    'distributors.partNumberDescription': 'Descripción',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.services': 'Services',
    'nav.products': 'Products',
    'nav.distributors': 'Distributors',
    'nav.blog': 'Blog',
    'nav.contact': 'Contact',
    'nav.careers': 'Careers',
    
    // Header Actions
    'header.newProject': 'New Project',
    'header.enterpriseSolutions': 'Enterprise Solutions',
    'header.contact': 'Contact',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.continue': 'Continue',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.reset': 'Reset',
    'common.submit': 'Submit',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.share': 'Share',

    'common.all_in': 'Everything in the',
    'common.more': 'plus',
    'common.read_more': 'See less features',
    'common.read_less': 'See more features',
    
    // Product Catalog
    'catalog.title': 'Product Catalog',
    'catalog.subtitle': 'Discover our selection of technological products',
    'catalog.allCategories': 'All categories',
    'catalog.filters.application': 'Application/Industry',
    'catalog.filters.processor': 'Processor',
    'catalog.filters.connectivity': 'Connectivity',
    'catalog.filters.temperature': 'Temperature Range',
    'catalog.filters.clear': 'Clear filters',
    'catalog.filters.clearAdvanced': 'Clear advanced filters',
    'catalog.results': 'Showing {count} of {total} products',
    'catalog.noResults': 'No products found in this category',
    'catalog.viewSpecs': 'View specifications',
    'catalog.download': 'Download Datasheet',
    'catalog.inStock': 'In Stock',
    'catalog.outOfStock': 'Out of Stock',
    'catalog.comingSoon': 'Coming Soon',
    'catalog.specifications': 'Technical Specifications',
    'catalog.features': 'Main Features',
    'catalog.applications': 'Applications',
    
    // Footer
    'footer.company': 'Company',
    'footer.services': 'Services',
    'footer.support': 'Support',
    'footer.legal': 'Legal',
    'footer.followUs': 'Follow Us',
    'footer.newsletter': 'Newsletter',
    'footer.newsletterText': 'Subscribe to receive the latest news',
    'footer.privacyPolicy': 'Privacy Policy',
    'footer.termsOfService': 'Terms of Service',
    'footer.cookiePolicy': 'Cookie Policy',
    
    // Cookie Consent
    'cookies.title': 'Cookie Usage',
    'cookies.message': 'We use cookies to enhance your browsing experience.',
    'cookies.accept': 'Accept',
    'cookies.decline': 'Decline',
    'cookies.configure': 'Configure',
    
    // Header buttons
    'buttons.new_project': 'New Project',
    'buttons.enterprise_solutions': 'Enterprise Solutions',
    'buttons.contact': 'B2B Contact',
    'buttons.cookies': 'Cookies',
    
    // Products
    'products.vending_machine': 'Vending Machine',
    'products.iot_meter': 'IoT Meter',
    'products.plannycs': 'Plannycs',
    'products.integrated_solutions': 'Integrated Solutions & Experiences',
    'products.specialized_components': 'Specialized Components & Accessories',
    'products.after_sales_service': 'After-sales & Local Technical Service',
    'products.system_integration': 'System Integration & Engineering Services',
    'products.cybersecurity': 'Cybersecurity & Industrial Security',
    'products.engineering_solutions': 'Engineering Solutions & Think Embedded Services',
    'products.ai_machine_learning': 'AI & Machine Learning Platforms',
    'products.cloud_edge_computing': 'Cloud & Edge Computing Services',
    'products.training_development': 'Training & Skills Development',
    'products.regulatory_compliance': 'Regulatory Compliance & Standards Certification',
    
    // Services
    'services.tech_stack': 'Technology Stack',
    'services.edge_management_platform': 'Edge Management Platform',
    'services.technical_consulting': 'Technical Consulting',
    'services.technical_service': 'Technical Service',
    'services.custom_solutions': 'Custom Solutions',
    'services.technical_quote': 'Technical Quote',
    
    // Company
    'company.compliance': 'Compliance',
    'company.supplier_portal': 'Supplier Portal',
    'company.supplier_registration': 'Supplier Registration',
    'company.client_onboarding': 'Client Onboarding',
    'company.knowledge_center': 'Knowledge Center',
    'company.vertical_solutions': 'Vertical Solutions',
    'company.innovation_lab': 'Innovation Lab',
    'company.quality_certifications': 'Quality Certifications',
    'company.white_papers': 'White Papers',
    'company.distributors': 'Distributors',
    'company.seco_distributors': 'SECO Distributors',
    'company.fannal_distributors': 'FANNAL Distributors',
    'company.udoo_distributors': 'UDOO Distributors',
    'company.partners': 'Partners',
    'company.customers': 'Customers',
    'company.faq' : 'Frequently Asked Questions',
    
    // Industries
    'industries.industry_40': 'Industry 4.0',
    'industries.healthcare': 'Healthcare',
    'industries.smart_infrastructure': 'Smart Infrastructure',
    'industries.transportation': 'Transportation',
    'industries.retail_hospitality': 'Retail & Hospitality',
    
    // Blog
    'blog.edge_ai_manufacturing': 'Edge AI in Manufacturing',
    'blog.iot_industrial_case_study': 'IoT Industrial Case Study',
    'blog.critical_firmware_development': 'Critical Firmware Development',
    'blog.mining_solutions': 'Mining Solutions',
    
    // Social Media
    'social.twitter': 'Twitter',
    'social.facebook': 'Facebook',
    'social.instagram': 'Instagram',
    'social.linkedin': 'LinkedIn',
    
    // Common
    'common.technical_glossary': 'Technical Glossary',
    'common.glossary': 'Glossary',
    'common.pricing': 'Pricing',
    'common.termsPrivacyForm' : 'To see how we handle the submitted information, refer to our',
    'common.addressCommercial': 'Commercial Address',
    'common.emails': 'Emails',
    'common.phones': 'Phones and Support Desk',
    'common.quotesPage': 'Product Quotes',
    
    // Language Selector
    'language.current': 'Current language',
    'language.changeTo': 'Change to',
    'language.spanish': 'Español',
    'language.english': 'English',
    
    // Navigation Sections
    'navigation.home': 'Home',
    'navigation.about': 'About Us',
    'navigation.services': 'Services',
    'navigation.products': 'Products & Services',
    'navigation.company': 'Company',
    'navigation.resources': 'Resources',
    'navigation.industry': 'Industries',
    'navigation.blog': 'Technical Blog',
    'navigation.careers': 'Careers',
    'navigation.more': '...',
    
    // Navigation Labels & Descriptions
    'navigation.company.about.label': 'About Us',
    'navigation.company.about.description': 'Learn more about our company',
    'navigation.company.compliance.label': 'Compliance',
    'navigation.company.compliance.description': 'Business compliance and regulations',
    'navigation.company.careers.label': 'Careers',
    'navigation.company.careers.description': 'Career opportunities at Polarycs',
    'navigation.products.view_all.title': 'View all products',
    'navigation.products.view_all.subtitle': 'Discover our range of specialized products and services',
    'navigation.products.edge_platform.title': 'Edge Platform',
    'navigation.products.edge_platform.description': 'Proprietary platform for edge device management and monitoring',
    'navigation.products.integrated_solutions.label': 'Integrated Solutions & Experiences',
    'navigation.products.integrated_solutions.description': 'Comprehensive personalized solutions for your business',
    'navigation.products.specialized_components.label': 'Specialized Components & Accessories',
    'navigation.products.specialized_components.description': 'High-quality hardware and components for IoT',
    'navigation.products.after_sales.label': 'After-sales & Local Technical Service',
    'navigation.products.after_sales.description': 'Specialized technical support and maintenance',
    'navigation.products.system_integration.label': 'System Integration & Engineering Services',
    'navigation.products.system_integration.description': 'System integration and specialized consulting',
    'navigation.products.cybersecurity.label': 'Cybersecurity & Industrial Security',
    'navigation.products.cybersecurity.description': 'Advanced protection for industrial systems',
    'navigation.products.engineering_solutions.label': 'Think Embedded Solutions & Services',
    'navigation.products.engineering_solutions.description': 'Engineering solutions and advanced embedded development',
    'navigation.products.ai_machine_learning.label': 'AI & Machine Learning Platforms',
    'navigation.products.ai_machine_learning.description': 'Applied artificial intelligence and machine learning',
    'navigation.products.cloud_edge.label': 'Cloud & Edge Computing Services',
    'navigation.products.cloud_edge.description': 'Cloud computing and edge processing',
    'navigation.products.training.label': 'Training & Skills Development',
    'navigation.products.training.description': 'Specialized training in IoT and Industry 4.0 technologies',
    'navigation.products.regulatory.label': 'Regulatory support & standards compliance',
    'navigation.products.regulatory.description': 'Regulatory compliance and standards certification.',
    'navigation.products.project_consulting.label': 'Project consulting & support',
    'navigation.products.project_consulting.description': 'Expert advice for the success of your technological projects',
    'navigation.resources.glossary.label': 'Glossary',
    'navigation.resources.glossary.description': 'Technical terms and specialized definitions',
    'navigation.resources.faq.label': 'Frequently Asked Questions',
    'navigation.resources.faq.description': 'Answers to common questions about our services',
    'navigation.resources.supplier_registration.label': 'Supplier Registration',
    'navigation.resources.tools.label': 'Tools',
    'navigation.resources.tools.description': 'Various tools and utilities for our services',
    'navigation.resources.supplier_registration.description': 'Portal for suppliers and business partners',
    'navigation.resources.client_onboarding.label': 'Client Onboarding',
    'navigation.resources.client_onboarding.description': 'New client onboarding process',
    'navigation.industries.industry40.label': 'Industry 4.0',
    'navigation.industries.industry40.description': 'Automation, predictive maintenance, AI quality control',
    'navigation.industries.healthcare.label': 'Healthcare',
    'navigation.industries.healthcare.description': 'Medical devices, patient monitoring, diagnostic imaging',
    'navigation.industries.smart_infrastructure.label': 'Smart Infrastructure',
    'navigation.industries.smart_infrastructure.description': 'Building automation, energy management, environmental monitoring',
    'navigation.industries.transportation.label': 'Transportation',
    'navigation.industries.transportation.description': 'Autonomous systems, fleet management, advanced telematics',
    'navigation.industries.retail.label': 'Retail & Hospitality',
    'navigation.industries.retail.description': 'Digital signage, customer analytics, automated inventory',
    'navigation.whitepaper.download': 'Download now',
    'navigation.whitepaper.title': 'Polarycs IoT White Paper',
    
    // Footer
    'footer.heading.title': 'Learn how to modernize your company',
    'footer.heading.cta': 'Start your project today',
    'footer.card1.title': 'Do you have a challenging industrial project? Get a free technical quote in 24 hours',
    'footer.card1.link': 'Quote my project now',
    'footer.card2.title': 'Discover how we transform companies with cutting-edge technology - Real case studies',
    'footer.card2.link': 'Explore success stories',
    'footer.statement': 'We transform industry and retail with embedded solutions, edge computing and technology integration, combining innovation and specialized support to maximize your company value.',
    'footer.address_cl': 'Av. Concon Reñaca 4000, Concón, Chile',
    'footer.address_au': '223 Liverpool Street, Darlinghurst NSW 2010, Australia',
    'footer.phone': '+123 456 7890',
    'footer.email': 'contacto@polarycs.com',
    'footer.section.company': 'Company',
    'footer.section.industries': 'Industries',
    'footer.section.resources': 'Resources',
    'footer.tracking_dhl' : 'DHL Tracking',
    'footer.section.legal': 'Legal',
    'footer.products_services': 'Products & Services',
    'footer.disclaimer': 'Polarycs S.p.A. - All rights reserved © {currentYear} - The information contained on this website is informational in nature and may be subject to change without prior notice. All trademarks, product names, logos and registered marks mentioned on this website are the property of their respective owners and are used solely for informational and identification purposes. SECO®, FANNAL®, UDOO®, Intel®, ARM®, Qualcomm®, NVIDIA®, Microsoft®, Amazon Web Services®, Google Cloud®, and other brands are the property of their respective companies. Polarycs S.p.A. acts as an authorized distributor and technology solutions provider, maintaining commercial agreements with various manufacturers and technology suppliers worldwide. Prices, product availability and technical specifications are subject to change without prior notice. Product images shown are for reference only and may differ from the final product. This website and all its content are protected by Chilean and international intellectual property laws. Total or partial reproduction of any material without express written authorization from Polarycs S.p.A. is prohibited. For inquiries about licenses, permissions or content usage, contact legal@polarycs.com.',

    // Products
    'products.cart': 'Quote Cart',
    'products.plural': 'products',
    'products.singular': 'product',
    'products.remove': 'Remove',
    'products.add_to_cart': 'Add to Cart',
    'products.remove_from_cart': 'Remove from Cart',
    'products.quantity': 'Quantity',
    'products.total': 'Total',
    'products.price': 'Price',
    'products.empty_cart': 'Your cart is empty',
    'products.proceed_to_checkout': 'Proceed to Quote',
    'products.send_quote': 'Send Quote Request',
    'products.quote_request': 'Quote Request',
    'products.sending': 'Sending...',
    'products.clean_cart': 'Clear Cart',
    'products.cancel': 'Cancel',
    'products.resume': 'Order Summary',
    'products.continue_shopping': 'Add products to request a quote',
    'products.form_contact': 'Contact Information',
    'products.form_name': 'First Name',
    'products.form_lastname': 'Last Name',
    'products.form_email': 'Email',
    'products.form_phone': 'Phone',
    'products.form_country': 'Country',
    'products.form_company': 'Company',
    'products.form_company_placeholder': 'Your company name',
    'products.form_message': 'Additional Message',
    'products.form_message_placeholder': 'Additional comments or special specifications...',
    'products.form_quantity': 'Quantity',
    'products.form_required': 'This field is required',
    'products.form_invalid_email': 'Invalid email address',
    'products.thank_you': 'Thank you for your request!',
    'products.confirmation_message': 'We have received your quote request. We will contact you soon.',

    // Demo Modal
    'demo.modal.title': 'Request Edge Platform Demo',
    'demo.modal.subtitle': 'Schedule a personalized demonstration with our team',
    'demo.personal.info': 'Personal Information',
    'demo.company.info': 'Company Information',
    'demo.plan.interest': 'Plan of Interest',
    'demo.plan.subtitle': 'Select the plan that best fits your needs',
    'demo.form.name': 'Full Name',
    'demo.form.name.placeholder': 'e.g., John Smith',
    'demo.form.email': 'Corporate Email',
    'demo.form.email.placeholder': 'john@company.com',
    'demo.form.phone': 'Phone',
    'demo.form.phone.placeholder': '+1 555 123 4567',
    'demo.form.country': 'Country',
    'demo.form.country.placeholder': 'Select country',
    'demo.form.role': 'Role/Position',
    'demo.form.role.placeholder': 'Select role',
    'demo.form.company': 'Company Name',
    'demo.form.company.placeholder': 'e.g., TechCorp Inc.',
    'demo.form.team.size': 'Team Size',
    'demo.form.team.size.placeholder': 'Select',
    'demo.form.industry': 'Industry',
    'demo.form.industry.placeholder': 'Select industry',
    'demo.form.message': 'Additional Message',
    'demo.form.message.placeholder': 'Tell us about your specific project, needs, or questions about Edge Platform...',
    'demo.form.required': 'is required',
    'demo.form.invalid.email': 'Invalid email',
    'demo.form.select.plan': 'Select a plan of interest',
    'demo.countries.us': 'United States',
    'demo.countries.cl': 'Chile',
    'demo.countries.ar': 'Argentina',
    'demo.countries.co': 'Colombia',
    'demo.countries.pe': 'Peru',
    'demo.countries.mx': 'Mexico',
    'demo.countries.ca': 'Canada',
    'demo.countries.au': 'Australia',
    'demo.countries.other': 'Other',
    'demo.roles.ceo': 'CEO/Founder',
    'demo.roles.cto': 'CTO',
    'demo.roles.it.director': 'IT Director',
    'demo.roles.product.manager': 'Product Manager',
    'demo.roles.engineer': 'Engineer',
    'demo.roles.operations': 'Operations Manager',
    'demo.roles.other': 'Other',
    'demo.team.1-10': '1-10 employees',
    'demo.team.11-50': '11-50 employees',
    'demo.team.51-200': '51-200 employees',
    'demo.team.201-500': '201-500 employees',
    'demo.team.500+': '500+ employees',
    'demo.industries.retail': 'Retail',
    'demo.industries.manufacturing': 'Manufacturing',
    'demo.industries.agriculture': 'Agriculture',
    'demo.industries.logistics': 'Logistics',
    'demo.industries.healthcare': 'Healthcare',
    'demo.industries.fintech': 'FinTech',
    'demo.industries.energy': 'Energy',
    'demo.industries.other': 'Other',
    'demo.success.title': 'Request Sent!',
    'demo.success.message': 'We have received your demo request. A Polarycs executive will contact you within the next 24 hours to coordinate the personalized demonstration.',
    'demo.success.email': 'Check your email for more details about the next step.',
    'demo.button.request': 'Request Demo',
    'demo.button.sending': 'Sending...',

    // Distributors
    'distributors.selectApplication': 'Select an application/industry',
    'distributors.selectProcessor': 'Select a processor',
    'distributors.selectConnectivity': 'Select a connectivity',
    'distributors.selectTemperature': 'Select a temperature range',
    'distributors.application': 'Application/Industry',
    'distributors.processor': 'Processor',
    'distributors.connectivity': 'Connectivity',
    'distributors.temperature': 'Temperature Range',
    'distributors.clearFilters': 'Clear filters',
    'distributors.clearAdvancedFilters': 'Clear advanced filters',
    'distributors.viewSpecs': 'View specifications',
    'distributors.download': 'Download Datasheet',
    'distributors.inStock': 'In Stock',
    'distributors.outOfStock': 'Out of Stock',
    'distributors.comingSoon': 'Coming Soon',
    'distributors.showProductsCount': 'Showing {count} of {total} products',
    'distributors.noProductsFound': 'No products available in this category',
    'distributors.principalFeatures': 'Main Features',
    'distributors.specifications': 'Technical Specifications',
    'distributors.details' : 'Details',
    'distributors.add': 'Add to Cart',
    'distributors.partNumbers': 'Part Numbers (SN\'s)',
    'distributors.partNumberHeader': 'Part Number',
    'distributors.partNumberDescription': 'Description',
  },
};

// Función para obtener el idioma del navegador
const getBrowserLanguage = (): Language => {
  if (typeof window === 'undefined') return 'es'; // SSR fallback
  
  const lang = navigator.language || (navigator as any).userLanguage;
  return lang.startsWith('en') ? 'en' : 'es';
};

// Función para gestionar cookies de idioma de forma segura
const LANGUAGE_COOKIE_NAME = 'polarycs-language';
const COOKIE_EXPIRY_DAYS = 365;

const getStoredLanguage = (): Language | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    // Prioridad: localStorage > cookie > navegador
    const localStorageLang = localStorage.getItem(LANGUAGE_COOKIE_NAME) as Language;
    if (localStorageLang && ['es', 'en'].includes(localStorageLang)) {
      return localStorageLang;
    }
    
    // Fallback a cookie
    const cookieLang = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${LANGUAGE_COOKIE_NAME}=`))
      ?.split('=')[1] as Language;
    
    if (cookieLang && ['es', 'en'].includes(cookieLang)) {
      return cookieLang;
    }
    
    return null;
  } catch (error) {
    console.warn('Error reading stored language:', error);
    return null;
  }
};

const storeLanguage = (language: Language): void => {
  if (typeof window === 'undefined') return;
  
  try {
    // Guardar en localStorage (más moderno y seguro)
    localStorage.setItem(LANGUAGE_COOKIE_NAME, language);
    
    // Backup en cookie para compatibilidad
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);
    
    document.cookie = `${LANGUAGE_COOKIE_NAME}=${language}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
  } catch (error) {
    console.warn('Error storing language preference:', error);
  }
};

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  defaultLanguage 
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Orden de prioridad: stored > defaultLanguage > browser > 'es'
    const storedLang = getStoredLanguage();
    if (storedLang) return storedLang;
    if (defaultLanguage) return defaultLanguage;
    return getBrowserLanguage() || 'es';
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simular carga inicial para evitar hidration mismatch
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    storeLanguage(newLanguage);
    
    // Disparar evento personalizado para notificar el cambio
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChange', { 
        detail: { language: newLanguage } 
      }));
    }
  };

  // Función de traducción con interpolación básica
  const t = (key: string, params?: Record<string, string | number>): string => {
    const languageTranslations = translations[language] as Record<string, string>;
    const fallbackTranslations = translations['es'] as Record<string, string>;
    
    let translation = languageTranslations[key] || fallbackTranslations[key] || key;
    
    // Interpolación simple para variables {variable}
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(
          new RegExp(`\\{${paramKey}\\}`, 'g'), 
          String(value)
        );
      });
    }
    
    return translation;
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t,
    isLoading
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook personalizado para usar el contexto de idioma
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Hook para traducción directa (más conveniente)
export const useTranslation = () => {
  const { t, language } = useLanguage();
  return { t, language };
};

// Export translations for static usage (already exported above as const)
